/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement } from "react"
import moment from "moment"
import {
  dispositionHasReviewerContent,
  PcgReviewRecordingBundle,
  recordingSiteLabel,
} from "@medaica/common/services/medaica-api-service/pcg-review-types"

/**
 * Review-state label of a recording for the head row and progress dots
 * (handoff v0.8.3 `stateLabels`: an accepted recording reads "Attested").
 */
type RecordingReviewState = "unreviewed" | "accepted" | "rejected" | "noted"

const REVIEW_STATE_LABELS: { [state in RecordingReviewState]: string } = {
  unreviewed: "Unreviewed",
  accepted: "Attested",
  rejected: "Rejected",
  noted: "Noted",
}

// Reviewer content = free-text note OR structured observations (v0.8.3.1).
const hasNote = (recording: PcgReviewRecordingBundle): boolean => dispositionHasReviewerContent(recording.disposition)

/**
 * Disposition → review state: a decision wins; a note without a decision is
 * "noted" (the handoff's dot statusClass rule); otherwise unreviewed.
 */
const recordingReviewState = (recording: PcgReviewRecordingBundle): RecordingReviewState => {
  const decision = recording.disposition?.decision
  if (decision === "accepted" || decision === "rejected") {
    return decision
  }
  return hasNote(recording) ? "noted" : "unreviewed"
}

/** Dot class for the handoff's `.exam-dot` state colours ("" when unreviewed). */
const reviewStateClass = (recording: PcgReviewRecordingBundle): string => {
  const state = recordingReviewState(recording)
  return state === "unreviewed" ? "" : state
}

/** Hover on the "Selected Recording —" title: how to move between and within recordings. */
const SELECTED_RECORDING_TIP =
  "Use the arrows, progress dots, or body map to change recording. " +
  "Click the waveform to seek · drag to pan · scroll or pinch to zoom · Space to play or pause"

/**
 * "Selected Recording —" head row of the exam workspace (handoff v0.8.3
 * `.selected-recording-head`): prev/next arrows, the "i/N" progress text,
 * the recording's site label, and one state-coloured dot per recording.
 * Rendered by the host into the viewer's `recordingHeader` slot; the viewer
 * appends its own Hover toggle after it.
 */
const SelectedRecordingNav = ({
  recordings,
  selectedAuscultationId,
  onSelect,
}: {
  recordings: PcgReviewRecordingBundle[]
  selectedAuscultationId: string | null
  onSelect: (auscultationId: string) => void
}): ReactElement => {
  const index = recordings.findIndex((recording) => recording.auscultationId === selectedAuscultationId)
  const selected = index >= 0 ? recordings[index] : null
  const total = recordings.length
  // Hover detail for the progress text: the recording's label plus its guide
  // provenance (the old page showed these as a text block above the viewer).
  const progressTitle = selected
    ? [
        `Recording ${index + 1} of ${total}`,
        recordingSiteLabel(selected),
        selected.recordingDuration ? `${Math.round(selected.recordingDuration)}s` : "",
        `Guide: ${selected.guide?.status ?? "pending"}${
          selected.guide?.guideVersion ? ` (${selected.guide.guideVersion})` : ""
        }`,
        selected.guide?.dateCreated ? moment(selected.guide.dateCreated).format("lll") : "",
      ]
        .filter(Boolean)
        .join(" · ")
    : ""

  return (
    <>
      <span className="selected-recording-title hover-help" data-hover={SELECTED_RECORDING_TIP}>
        Selected Recording —
      </span>
      <div className="exam-progress-controls">
        <button
          className="exam-arrow"
          type="button"
          aria-label="Previous recording"
          disabled={index <= 0}
          onClick={() => onSelect(recordings[index - 1].auscultationId)}
        >
          ←
        </button>
        <span className="exam-progress-text" title={progressTitle}>
          {index >= 0 ? `${index + 1}/${total}` : `–/${total}`}
        </span>
        <span className="exam-site-label" title={progressTitle}>
          {selected ? recordingSiteLabel(selected) : ""}
        </span>
        <button
          className="exam-arrow"
          type="button"
          aria-label="Next recording"
          disabled={index < 0 || index >= total - 1}
          onClick={() => onSelect(recordings[index + 1].auscultationId)}
        >
          →
        </button>
      </div>
      <div className="exam-progress-dots" data-testid="exam-progress-dots">
        {recordings.map((recording, i) => {
          const state = recordingReviewState(recording)
          const title =
            `Recording ${i + 1} of ${total} · ${recordingSiteLabel(recording)} · ${REVIEW_STATE_LABELS[state]}` +
            (hasNote(recording) ? " · Note" : "")
          const classes = [
            "exam-dot",
            reviewStateClass(recording),
            recording.auscultationId === selectedAuscultationId ? "active" : "",
          ]
            .filter(Boolean)
            .join(" ")
          return (
            <button
              key={recording.auscultationId}
              className={classes}
              type="button"
              title={title}
              aria-label={title}
              aria-current={recording.auscultationId === selectedAuscultationId || undefined}
              onClick={() => onSelect(recording.auscultationId)}
            />
          )
        })}
      </div>
    </>
  )
}

export default SelectedRecordingNav
export { recordingReviewState, REVIEW_STATE_LABELS }
export type { RecordingReviewState }
